"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { Snackbar } from "@/components/ui/snackbar";

/**
 * Product toast host (handoff §4).
 *
 * The DS ships only the presentational `@tb/snackbar`, so what lives here is the behaviour:
 * queue, timer, position and a single live region.
 */

/**
 * How long a toast stays — **and the only number that decides it**, for every variant.
 *
 * The handoff (§4, §11) said 2600ms, which was right while nothing waited for it. Then
 * navigation began consulting the toast before moving (`whenRead`) with the monogram's veil
 * after it, so the dwell started adding to the trip: 2600 + 220 to fade + 200 to cover + the
 * next page's fetch + 380 to reveal put the destination past three and a half seconds. It was
 * cut to 1200ms on 2026-10-09 to buy that back.
 *
 * **1200 was too little** (ruling of 2026-10-14): it read as hurry, as if the message were
 * being taken away mid-sentence. A full line like "Confira os dados e tente de novo." needs
 * more than a glance. 2000ms was tried as a middle ground, but it was chosen without checking
 * who authored the number: the designer's handoff documents 2600ms
 * (`Painel do Anfitriao - handoff.md:130` and `:465`), and the designer's number prevails.
 *
 * The cost is declared: where the toast holds navigation (`useLeaveFor`) the trip grows to
 * about 2.8s, and about 3.4s with the loading veil, between the click and the new screen.
 *
 * The countdown bar reads this same constant, so the two can never disagree.
 */
export const TOAST_DURATION_MS = 2600;

/**
 * How long the toast takes to fade out, mirroring `--tb-duration-2` (220ms).
 *
 * The number lives here **and** as a token in `globals.css` because one side animates and the
 * other has to know when the animation is over. `app/__tests__/design-tokens.test.ts` is where
 * that pair is kept honest.
 */
export const TOAST_EXIT_MS = 220;

/**
 * The longest navigation will ever wait for a toast.
 *
 * Covers one whole life — dwell **plus** the fade — because the rule is that the screen never
 * changes under a toast that is still visible (ruling of 2026-10-09). A cap shorter than that
 * would cut the fade and produce exactly the abrupt cut it exists to prevent.
 *
 * Still a separate constant from the dwell: the dwell is a reading decision, this is a patience
 * budget. Someone who presses an action and immediately presses "voltar" must not feel a hang,
 * so a pile of messages cannot extend the wait without end.
 */
export const NAVIGATION_HOLD_MAX_MS = TOAST_DURATION_MS + TOAST_EXIT_MS;

/**
 * Three is what fits below the header without reaching the content, so a fast sequence of
 * actions cannot turn into a wall of notices.
 */
const MAX_VISIBLE_TOASTS = 3;

type ToastVariant = "success" | "neutral" | "error";

type ToastOptions = {
  variant?: ToastVariant;
  description?: string;
};

type Toast = {
  id: number;
  message: string;
  variant: ToastVariant;
  description?: string;
  /**
   * When this toast stops asking to be read, as a timestamp.
   *
   * An **error** never holds navigation: an error means the person is staying, and the
   * destination may well be wrong precisely because the write failed. So only the other two
   * variants carry a deadline.
   */
  holdsUntil: number | null;
  /** True once the dwell is over and it is fading. Nothing navigates while this is on screen. */
  isLeaving: boolean;
};

type ToastContextValue = {
  showToast: (message: string, options?: ToastOptions) => void;
  /**
   * Resolves when nothing is waiting to be read — immediately when nothing is.
   *
   * This is the whole point of the toast living in one place: the host owns the timers, so it is
   * the only thing that can answer "is there a message on screen that has not had its moment?".
   * Before, every call site had to remember to wait, and `leaveFor`, `CreateListTerms` and the
   * PDP each forgot in their own way.
   *
   * Capped at {@link NAVIGATION_HOLD_MAX_MS}: a wait nobody budgeted for reads as a hang.
   */
  whenRead: () => Promise<void>;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);

  if (context === null) {
    throw new Error("useToast must be used inside a ToastProvider.");
  }

  return context;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  /** When the last message that asks to be read stops asking. `0` means nothing is waiting. */
  const holdUntil = useRef(0);

  const forget = useCallback((id: number) => {
    const timer = timers.current.get(id);

    if (timer !== undefined) {
      clearTimeout(timer);
      timers.current.delete(id);
    }

    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  /**
   * Ends a toast in two beats: it fades, and only then is it gone.
   *
   * The two beats are the point. Navigation waits for **both** (`whenRead` covers dwell plus
   * fade), so the screen never changes under a message still on screen — which is what made the
   * old behaviour feel like a cut rather than a goodbye (reported 2026-10-09).
   */
  const beginLeaving = useCallback(
    (id: number) => {
      setToasts((current) =>
        current.map((toast) => (toast.id === id ? { ...toast, isLeaving: true } : toast)),
      );

      timers.current.set(
        id,
        setTimeout(() => forget(id), TOAST_EXIT_MS),
      );
    },
    [forget],
  );

  const showToast = useCallback(
    (message: string, options?: ToastOptions) => {
      const id = nextId.current;
      nextId.current += 1;

      const variant = options?.variant ?? "neutral";
      const holdsUntil = variant === "error" ? null : Date.now() + TOAST_DURATION_MS;
      const toast: Toast = {
        id,
        message,
        variant,
        description: options?.description,
        holdsUntil,
        isLeaving: false,
      };

      /*
       * The deadline is written **here**, in the handler, and not derived from state during
       * render: a screen that announces and leaves in the same tick would otherwise ask
       * `whenRead` before React had committed the toast, and be told there was nothing to wait
       * for. Latest deadline wins, so a second message extends the wait rather than shortening
       * it.
       */
      if (holdsUntil !== null) {
        // The fade counts: nothing moves while the message is still visible.
        holdUntil.current = Math.max(holdUntil.current, holdsUntil + TOAST_EXIT_MS);
      }

      setToasts((current) => {
        const next = [...current, toast];
        // Drops the oldest ones, cancelling the timers left behind.
        const dropped = next.slice(0, Math.max(0, next.length - MAX_VISIBLE_TOASTS));

        for (const stale of dropped) {
          const timer = timers.current.get(stale.id);
          if (timer !== undefined) {
            clearTimeout(timer);
            timers.current.delete(stale.id);
          }
        }

        return next.slice(-MAX_VISIBLE_TOASTS);
      });

      timers.current.set(
        id,
        setTimeout(() => beginLeaving(id), TOAST_DURATION_MS),
      );
    },
    [beginLeaving],
  );

  // A toast in flight must not outlive unmount: the timer would setState on a removed
  // component.
  useEffect(() => {
    const pending = timers.current;

    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const whenRead = useCallback((): Promise<void> => {
    const remaining = holdUntil.current - Date.now();
    // A deadline in the past needs no branch of its own: it simply has nothing left to wait for.
    if (remaining <= 0) return Promise.resolve();

    return new Promise((resolve) => {
      setTimeout(resolve, Math.min(remaining, NAVIGATION_HOLD_MAX_MS));
    });
  }, []);

  const value = useMemo(() => ({ showToast, whenRead }), [showToast, whenRead]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/*
       * One persistent live region, rendered even while empty: what gets announced is a change
       * inside an `aria-live` that already existed, not the insertion of a new one.
       *
       * No portal on purpose. The provider mounts in the root layout, a direct child of
       * `<body>`, so no ancestor `transform`, `filter` or `contain` can capture the fixed
       * position — and the region ships in the server HTML, with no `document` guard.
       */}
      <div
        data-toast-viewport=""
        role="status"
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed left-1/2 top-24 z-[430] flex -translate-x-1/2 flex-col items-center gap-2"
      >
        {toasts.map((toast) => (
          /*
           * The countdown is drawn **over** the card rather than inside it: `Snackbar` is the
           * DS's presentational component and a countdown is behaviour made visible, which is
           * the host's half of the deal (ds-variant-candidates, T3). `overflow-hidden` with the
           * card's own radius keeps the bar inside the rounded corners.
           */
          <div
            key={toast.id}
            data-toast-leaving={toast.isLeaving ? "" : undefined}
            // What the left rule reads to pick its colour (`globals.css`, `.tb-toast-card::after`).
            data-toast-variant={toast.variant}
            className="tb-toast-card"
          >
            {/*
             * **Always `neutral`**, which is the bare card — the 3px green rule on the left is
             * the host's to draw (`globals.css`), for every variant.
             *
             * The handoff gives that rule to the toast with no condition attached
             * (`Painel do Anfitriao - handoff.md:128`), while `Snackbar` offers it only on
             * `success` and leaves `neutral` bare. Asking for the real variant here meant 19 of
             * the 34 `showToast` calls drew no rule, the "quero essa lista" flow among them.
             *
             * `Snackbar`'s own variants are left exactly as they are: the checkout's reversible
             * snackbar is another surface, and it still wants a bare `neutral`.
             */}
            <Snackbar
              variant="neutral"
              title={toast.message}
              description={toast.description}
            />
            {/*
             * On **every** toast, success or error, and always measuring the same thing: how
             * long this message stays. That is the whole rule (ruling of 2026-10-14).
             *
             * It used to be drawn only on a toast that gated a navigation, which made the bar
             * mean two things at once — "time left" and "then we move" — and left the error
             * toast with no bar at all. With no countdown to look at, the same dwell read as
             * faster than the others, which is how this was reported.
             *
             * Whether navigation waits is a **separate** fact, and it still is: an error does
             * not hold (`holdsUntil`), because an error means the person is staying.
             */}
            <span
              aria-hidden="true"
              data-toast-countdown=""
              className="tb-toast-countdown"
              style={{ "--tb-toast-dwell": `${TOAST_DURATION_MS}ms` } as React.CSSProperties}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
